import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  LockOutlined as LockOutlinedIcon,
  AttachMoney as AttachMoneyIcon,
  Description as DescriptionIcon,
} from '@mui/icons-material';
import { tienePermisosModulo, puedeEditarProgramaContabilidad } from 'lib/seguridad/permisosPorModulo';
import { useVersionContable } from './versionContable';
import { FP, PageHeader, StatCard, fpInputSx } from 'components/financepro';
import { useProductosMaterialContext } from 'context';
import PrintListado from 'utils/PrintListado';

const PROGRAMA = 'mantenimientonotascreditosuplidor';

const fmtMoney = (v) =>
  (parseFloat(v) || 0).toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const opcionLabel = (s) => {
  if (!s) return '';
  const id = s.id != null ? String(s.id) : '';
  const d = (s.descrip || '').trim();
  const r = (s.rnc || '').trim();
  return `${id}-${d}-${r}`;
};

const formatearFecha = (fecha) => {
  if (!fecha) return '-';
  try {
    const d = new Date(fecha);
    return d.toLocaleDateString('es-ES', { year: 'numeric', month: '2-digit', day: '2-digit' });
  } catch {
    return fecha;
  }
};

const fechaLocalHoy = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const primerDiaAno = () => {
  const d = new Date();
  return `${d.getFullYear()}-01-01`;
};

const estadoNc = (status) => (String(status || '').toUpperCase() === 'S' ? 'Aplicada' : 'Pendiente');

const totalNc = (row) => (parseFloat(row.precio) || 0) + (parseFloat(row.itbis) || 0);

const btnPrimarySx = {
  height: 46,
  bgcolor: FP.color.navy,
  color: '#FFFFFF',
  borderRadius: 1.4,
  textTransform: 'none',
  fontWeight: 700,
  boxShadow: 'none',
  '&:hover': { bgcolor: FP.color.navyDeep, boxShadow: 'none' },
};

const btnOutlinedSx = {
  height: 46,
  borderColor: FP.color.border,
  color: FP.color.textPrimary,
  bgcolor: '#FFFFFF',
  borderRadius: 1.6,
  textTransform: 'none',
  fontWeight: 700,
  '&:hover': { bgcolor: FP.color.surfaceAlt, borderColor: FP.color.borderStrong },
};

const MantenimientoNotaCreditoSuplidor = () => {
  const { controller, selectedCompany, showSuccess, showError } = useProductosMaterialContext();
  const { nombreNegocio, usuarioLog, claveLog, roleDetalles } = controller;
  const servidor = process.env.REACT_APP_SERVIDOR || '';

  const puedeLeer = tienePermisosModulo(roleDetalles, PROGRAMA);
  // La versión fiscal es solo de consulta: ni registrar ni anular.
  const { soloConsulta } = useVersionContable();
  const puedeModificar =
    !soloConsulta && puedeEditarProgramaContabilidad(roleDetalles, PROGRAMA);
  const puedeOperarDb = Boolean(selectedCompany?.db_empresa);

  const [suplidores, setSuplidores] = useState([]);
  const [suplidorSel, setSuplidorSel] = useState(null);
  const [cargandoSupl, setCargandoSupl] = useState(false);
  const [filas, setFilas] = useState([]);
  const [cargando, setCargando] = useState(false);

  const [fechaDesde, setFechaDesde] = useState(primerDiaAno);
  const [fechaHasta, setFechaHasta] = useState(fechaLocalHoy);

  const cargarSuplidores = useCallback(async () => {
    if (!puedeLeer || !puedeOperarDb) return;
    setCargandoSupl(true);
    try {
      const response = await fetch(`${servidor}/contabilidadbackend_listarsuplidores`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id_empresa: selectedCompany,
          usuario: usuarioLog || '',
          password: claveLog || '',
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        showError(data.Error || 'No se pudieron cargar suplidores');
        setSuplidores([]);
        return;
      }
      setSuplidores(data.suplidores || []);
    } catch {
      setSuplidores([]);
      showError('Error de conexión al cargar suplidores');
    } finally {
      setCargandoSupl(false);
    }
  }, [puedeLeer, puedeOperarDb, servidor, selectedCompany, usuarioLog, claveLog, showError]);

  const buscar = useCallback(async () => {
    if (!puedeLeer || !puedeOperarDb) return;
    setCargando(true);
    try {
      const body = {
        id_empresa: selectedCompany,
        usuario: usuarioLog || '',
        password: claveLog || '',
        fecha_desde: (fechaDesde || '').trim(),
        fecha_hasta: (fechaHasta || '').trim(),
        limite: 2000,
      };
      if (suplidorSel?.id) body.suplidor_id = String(suplidorSel.id);
      const response = await fetch(`${servidor}/contabilidadbackend_listarmantenimientonotacreditosuplidor`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        showError(data.Error || 'Error al cargar notas de crédito');
        setFilas([]);
        return;
      }
      setFilas(data.notas || []);
    } catch {
      setFilas([]);
      showError('Error de conexión al cargar notas de crédito');
    } finally {
      setCargando(false);
    }
  }, [puedeLeer, puedeOperarDb, servidor, selectedCompany, usuarioLog, claveLog, fechaDesde, fechaHasta, suplidorSel, showError]);

  useEffect(() => {
    if (puedeLeer && puedeOperarDb) {
      cargarSuplidores();
    }
  }, [puedeLeer, puedeOperarDb, cargarSuplidores]);

  useEffect(() => {
    if (puedeLeer && puedeOperarDb) {
      buscar();
    }
  }, [puedeLeer, puedeOperarDb]);

  const eliminarNota = async (row) => {
    if (!puedeModificar) {
      showError('No tiene permiso para eliminar');
      return;
    }
    if (!row.puede_eliminar) {
      showError(
        'No se puede eliminar: si la nota está aplicada a una factura CxP, esa factura tiene pagos registrados en pagossup o no se encontró la factura.'
      );
      return;
    }
    const ok = window.confirm(
      `¿Eliminar la nota de crédito id ${row.id} (ncred ${row.ncred || '—'}) del suplidor ${row.nombre_suplidor || row.suplidor}? Esta acción no se puede deshacer.`
    );
    if (!ok) return;
    try {
      const response = await fetch(`${servidor}/contabilidadbackend_eliminarnotacreditosuplidor`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id_empresa: selectedCompany,
          nota_id: row.id,
          usuario: usuarioLog || '',
          password: claveLog || '',
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        showError(data.Error || 'No se pudo eliminar');
        return;
      }
      showSuccess(data.mensaje || 'Nota de crédito eliminada');
      await buscar();
    } catch {
      showError('Error de conexión al eliminar');
    }
  };

  const nombreEmpresa =
    selectedCompany?.name || selectedCompany?.empresa || nombreNegocio || 'Empresa';

  const subtituloPrint = useMemo(() => {
    const sup = suplidorSel ? opcionLabel(suplidorSel) : 'Todos los suplidores';
    const fd = fechaDesde ? `Desde ${formatearFecha(fechaDesde)}` : '';
    const fh = fechaHasta ? `Hasta ${formatearFecha(fechaHasta)}` : '';
    return `${sup} · ${fd} · ${fh} · ${filas.length} registro(s)`;
  }, [suplidorSel, fechaDesde, fechaHasta, filas.length]);

  const columnasPrint = [
    { id: 'id', label: 'Id', align: 'center' },
    { id: 'ncred', label: 'Ncred', align: 'center' },
    { id: 'suplidor', label: 'Suplidor', align: 'center' },
    { id: 'nombre_suplidor', label: 'Nombre' },
    { id: 'fecha', label: 'Fecha', align: 'center', render: (_, row) => formatearFecha(row.fecha) },
    { id: 'precio', label: 'Precio', align: 'right', render: (v) => `$${fmtMoney(v)}` },
    { id: 'itbis', label: 'ITBIS', align: 'right', render: (v) => `$${fmtMoney(v)}` },
    {
      id: 'total_nc',
      label: 'Total',
      align: 'right',
      render: (_, row) => `$${fmtMoney(totalNc(row))}`,
    },
    { id: 'ncf', label: 'NCF', align: 'center' },
    { id: 'descripcion', label: 'Descripción', align: 'left' },
    { id: 'status', label: 'Estado', align: 'center', render: (v) => estadoNc(v) },
    { id: 'nfactura_aplicada', label: 'Fact. aplicación', align: 'center' },
    {
      id: 'pagos_pagossup',
      label: 'Pagos (pagossup)',
      align: 'right',
      render: (v) => (v == null ? '—' : `$${fmtMoney(v)}`),
    },
    {
      id: 'puede_eliminar',
      label: '¿Eliminable?',
      align: 'center',
      render: (v) => (v ? 'Sí' : 'No'),
    },
  ];

  const totalMontoReporte = useMemo(() => filas.reduce((s, r) => s + totalNc(r), 0), [filas]);

  const cardShellSx = {
    borderRadius: 2.5,
    border: `1px solid ${FP.color.border}`,
    bgcolor: FP.color.cardBg,
    boxShadow: FP.shadow.soft,
    overflow: 'hidden',
  };

  const filterHeaderSx = {
    px: { xs: 2, md: 3 },
    py: 1.6,
    borderBottom: `1px solid ${FP.color.border}`,
    bgcolor: '#FAFBFE',
    display: 'flex',
    alignItems: 'center',
    gap: 1.2,
  };

  const cellHeadSx = {
    fontWeight: 700,
    fontSize: '0.75rem',
    color: FP.color.textSecondary,
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
    whiteSpace: 'nowrap',
  };

  if (!puedeLeer) {
    return (
      <Box component="main" sx={{ minHeight: '100vh', bgcolor: FP.color.surface }}>
        <PageHeader
          title="Mantenimiento de notas de crédito suplidor"
          subtitle={nombreNegocio ? `${nombreNegocio} · N/C suplidor` : 'N/C suplidor'}
        />
        <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 }, display: 'flex', justifyContent: 'center' }}>
          <Card
            elevation={0}
            sx={{ ...cardShellSx, maxWidth: 560, width: '100%', textAlign: 'center', p: { xs: 3, md: 4 } }}
          >
            <Box
              sx={{
                width: 64,
                height: 64,
                borderRadius: 1.6,
                mx: 'auto',
                mb: 2.5,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: FP.color.dangerSoft,
                color: FP.color.danger,
              }}
            >
              <LockOutlinedIcon sx={{ fontSize: 30 }} />
            </Box>
            <Typography
              sx={{
                fontWeight: 800,
                fontSize: '1.25rem',
                color: FP.color.textPrimary,
                mb: 1,
                letterSpacing: '-0.01em',
              }}
            >
              Acceso denegado
            </Typography>
            <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.95rem', mb: 1.2 }}>
              No tiene permiso del programa «mantenimientonotascreditosuplidor» en roles para acceder al mantenimiento.
            </Typography>
            <Typography sx={{ color: FP.color.textMuted, fontSize: '0.85rem' }}>
              Por favor, contacte al administrador del sistema para solicitar los permisos necesarios.
            </Typography>
          </Card>
        </Box>
      </Box>
    );
  }

  if (!puedeOperarDb) {
    return (
      <Box component="main" sx={{ minHeight: '100vh', bgcolor: FP.color.surface }}>
        <PageHeader
          title="Mantenimiento de notas de crédito suplidor"
          subtitle={nombreNegocio ? `${nombreNegocio} · N/C suplidor` : 'N/C suplidor'}
        />
        <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
          <Alert severity="info">Seleccione una empresa con base de datos asignada.</Alert>
        </Box>
      </Box>
    );
  }

  return (
    <Box component="main" sx={{ minHeight: '100vh', bgcolor: FP.color.surface }}>
      <PageHeader
        title="Mantenimiento de notas de crédito suplidor"
        subtitle={
          nombreNegocio
            ? `${nombreNegocio} · Tabla notacreditosuplidor`
            : 'Tabla notacreditosuplidor'
        }
      />

      <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
        <Card elevation={0} sx={{ ...cardShellSx, mb: 3 }}>
          <Box sx={filterHeaderSx}>
            <Box
              sx={{
                width: 30,
                height: 30,
                borderRadius: 1.2,
                bgcolor: FP.color.accentBlueSoft,
                color: FP.color.accentBlue,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <SearchIcon sx={{ fontSize: 18 }} />
            </Box>
            <Typography sx={{ fontWeight: 700, color: FP.color.textPrimary, fontSize: '0.98rem' }}>
              Filtros de búsqueda
            </Typography>
            <Box sx={{ flex: 1 }} />
            <Box className="no-print" sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <PrintListado
                title={nombreEmpresa}
                reportTitle="Listado — mantenimiento N/C suplidor"
                subtitle={subtituloPrint}
                columns={columnasPrint}
                data={filas}
                emptyMessage="No hay datos para imprimir"
                footer={`Total (precio + ITBIS): $${fmtMoney(totalMontoReporte)}`}
                printButtonLabel="Imprimir listado"
                printButtonProps={{
                  disabled: filas.length === 0,
                  sx: {
                    textTransform: 'none',
                    fontWeight: 700,
                    borderRadius: 1.4,
                    bgcolor: FP.color.navy,
                    color: '#FFFFFF',
                    boxShadow: 'none',
                    '&:hover': { bgcolor: FP.color.navyDeep, boxShadow: 'none' },
                  },
                }}
              />
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={() => {
                  cargarSuplidores();
                  buscar();
                }}
                disabled={cargando}
                sx={btnOutlinedSx}
              >
                Actualizar
              </Button>
            </Box>
          </Box>
          <CardContent sx={{ p: { xs: 2, md: 3 } }} className="no-print">
            <Grid container spacing={2.5} alignItems="flex-end">
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  type="date"
                  label="Fecha desde"
                  value={fechaDesde}
                  onChange={(e) => setFechaDesde(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={fpInputSx}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  type="date"
                  label="Fecha hasta"
                  value={fechaHasta}
                  onChange={(e) => setFechaHasta(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={fpInputSx}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Autocomplete
                  options={suplidores}
                  loading={cargandoSupl}
                  getOptionLabel={(o) => opcionLabel(o)}
                  value={suplidorSel}
                  onChange={(_, v) => setSuplidorSel(v)}
                  isOptionEqualToValue={(a, b) => a?.id === b?.id}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Suplidor (opcional)"
                      placeholder="Todos"
                      InputLabelProps={{ shrink: true }}
                      sx={fpInputSx}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <Button
                  variant="contained"
                  startIcon={<SearchIcon />}
                  onClick={() => buscar()}
                  disabled={cargando}
                  sx={btnPrimarySx}
                >
                  Buscar
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        <Alert
          severity="info"
          sx={{
            mb: 3,
            borderRadius: 2,
            border: `1px solid ${FP.color.border}`,
            bgcolor: FP.color.cardBg,
          }}
        >
          Se puede eliminar una nota si está <strong>pendiente de aplicar</strong>, o si está <strong>aplicada</strong>{' '}
          y la factura Cuentas x Pagar vinculada no tiene <strong>pagos en pagossup</strong> (suma de valorpagado para
          esa factura y suplidor). Al eliminar una nota aplicada, se descuenta precio + ITBIS del campo{' '}
          <strong>ncpagada</strong> de esa factura.
        </Alert>

        {filas.length > 0 && (
          <Grid container spacing={2} sx={{ mb: 3 }} className="no-print">
            <Grid item xs={12} sm={6} md={6}>
              <StatCard
                label="Registros"
                value={String(filas.length)}
                icon={DescriptionIcon}
                accent={FP.color.accentBlue}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={6}>
              <StatCard
                label="Total (precio + ITBIS)"
                value={`$${fmtMoney(totalMontoReporte)}`}
                icon={AttachMoneyIcon}
                accent={FP.color.navy}
              />
            </Grid>
          </Grid>
        )}

        <Card elevation={0} sx={cardShellSx}>
          <Box
            sx={{
              px: { xs: 2, md: 3 },
              py: 1.6,
              borderBottom: `1px solid ${FP.color.border}`,
              bgcolor: '#FAFBFE',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1,
              flexWrap: 'wrap',
            }}
          >
            <Typography sx={{ fontWeight: 700, color: FP.color.textPrimary, fontSize: '0.98rem' }}>
              Detalle de notas de crédito suplidor
            </Typography>
            {cargando && (
              <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.82rem', fontWeight: 600 }}>
                Cargando…
              </Typography>
            )}
          </Box>
          <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
            {cargando ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                <CircularProgress size={28} sx={{ color: FP.color.accentBlue }} />
              </Box>
            ) : (
              <TableContainer
                component={Paper}
                elevation={0}
                sx={{ border: `1px solid ${FP.color.border}`, borderRadius: 2, overflow: 'auto' }}
              >
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow
                      sx={{
                        '& th': {
                          bgcolor: FP.color.surfaceAlt,
                          borderBottom: `2px solid ${FP.color.border}`,
                        },
                      }}
                    >
                      <TableCell align="center" sx={cellHeadSx}>
                        Id
                      </TableCell>
                      <TableCell align="center" sx={cellHeadSx}>
                        Ncred
                      </TableCell>
                      <TableCell sx={cellHeadSx}>Suplidor</TableCell>
                      <TableCell sx={cellHeadSx}>Fecha</TableCell>
                      <TableCell align="right" sx={cellHeadSx}>
                        Precio
                      </TableCell>
                      <TableCell align="right" sx={cellHeadSx}>
                        ITBIS
                      </TableCell>
                      <TableCell align="right" sx={cellHeadSx}>
                        Total
                      </TableCell>
                      <TableCell sx={cellHeadSx}>NCF</TableCell>
                      <TableCell sx={cellHeadSx}>Descripción</TableCell>
                      <TableCell sx={cellHeadSx}>Estado</TableCell>
                      <TableCell sx={cellHeadSx}>Fact. aplicación</TableCell>
                      <TableCell align="right" sx={cellHeadSx}>
                        Pagos (pagossup)
                      </TableCell>
                      <TableCell align="center" width={56} sx={cellHeadSx}>
                        {' '}
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filas.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={13}>
                          <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.9rem', py: 2 }}>
                            No hay registros con los filtros indicados.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      filas.map((row) => (
                        <TableRow key={row.id}>
                          <TableCell align="center">{row.id}</TableCell>
                          <TableCell align="center">{row.ncred || '—'}</TableCell>
                          <TableCell>{row.nombre_suplidor || row.suplidor}</TableCell>
                          <TableCell>{formatearFecha(row.fecha)}</TableCell>
                          <TableCell align="right">${fmtMoney(row.precio)}</TableCell>
                          <TableCell align="right">${fmtMoney(row.itbis)}</TableCell>
                          <TableCell align="right">${fmtMoney(totalNc(row))}</TableCell>
                          <TableCell>{row.ncf || '—'}</TableCell>
                          <TableCell>{row.descripcion || '—'}</TableCell>
                          <TableCell>{estadoNc(row.status)}</TableCell>
                          <TableCell>{row.nfactura_aplicada || '—'}</TableCell>
                          <TableCell align="right">
                            {row.pagos_pagossup == null ? '—' : `$${fmtMoney(row.pagos_pagossup)}`}
                          </TableCell>
                          <TableCell align="center">
                            <IconButton
                              size="small"
                              color="error"
                              disabled={!puedeModificar || !row.puede_eliminar}
                              onClick={() => eliminarNota(row)}
                              aria-label="Eliminar nota de crédito"
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};

export default MantenimientoNotaCreditoSuplidor;
